import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { ethers } from 'ethers';

const Header = ({ user, notifications = [], onNotificationClick, onProfileClick }) => {
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const [metamaskAddress, setMetamaskAddress] = useState(null);
  
  const navigationItems = [
    { path: '/member-dashboard', label: 'Dashboard', icon: 'Home' },
    { path: '/progress-tracking', label: 'Progress', icon: 'TrendingUp' },
    { path: '/community-forums', label: 'Community', icon: 'Users' },
    { path: '/wellness-marketplace', label: 'Marketplace', icon: 'ShoppingBag' },
    { path: '/member-profile', label: 'Profile', icon: 'User' }
  ];

  useEffect(() => {
    if (localStorage.getItem('digitalwellnessmetamaskAddress')) {
      setMetamaskAddress(localStorage.getItem('digitalwellnessmetamaskAddress'));
    } else {
      setMetamaskAddress(null);
    }
  }, []);

  const connectMetamask = async () => {
    if (window.ethereum) {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      if (signer) {
        setMetamaskAddress(signer.address);
        localStorage.setItem('digitalwellnessmetamaskAddress', signer.address);
        window.location.reload();
      }
    } else {
      alert('Metamask not installed');
    }
  }

  const metamaskLogout = () => {
    localStorage.removeItem('digitalwellnessmetamaskAddress');
    setMetamaskAddress(null);
    window.location.reload();
  }

  const isActive = (path) => {
    if (path === '/community-forums' && location?.pathname === '/events-and-workshops') {
      return true;
    }
    return location?.pathname === path;
  };

  const handleNavigation = (path) => {
    window.location.href = path;
    setIsMobileMenuOpen(false);
  };

  const unreadCount = notifications?.filter(n => !n?.read)?.length;

  return (
    <header className="sticky top-0 z-50 w-full bg-card border-b border-border shadow-wellness">
      <div className="flex h-16 items-center px-4 lg:px-6">
        {/* Logo */}
        <div className="flex items-center">
          <button 
            onClick={() => handleNavigation('/member-dashboard')}
            className="flex items-center space-x-2 transition-wellness hover:opacity-80"
          >
            <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
              <Icon name="Heart" size={20} color="white" />
            </div>
            <span className="font-heading font-semibold text-lg text-foreground hidden sm:block">
              Digital Wellness Bank
            </span>
          </button>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-1 ml-8">
          {navigationItems?.map((item) => (
            <button
              key={item?.path}
              onClick={() => handleNavigation(item?.path)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-wellness ${
                isActive(item?.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={item?.icon} size={16} />
              <span>{item?.label}</span>
            </button>
          ))}
        </nav>

        <div>
          {metamaskAddress && (
            <div className="flex items-center space-x-2 ml-auto">
              <div className="flex items-center space-x-2 px-3 py-1 bg-accent/10 rounded-full">
                <div className="w-2 h-2 bg-accent rounded-full animate-breathing"></div>
                <span className="text-sm font-caption text-accent-foreground font-medium">
                  Metamask Connected {metamaskAddress}
                  <button className=" rounded-full bg-accent px-2 py-1 ml-2 cursor-pointer text-xs text-muted-foreground" onClick={metamaskLogout}>Logout Metamask</button>
                </span>
              </div>
            </div>
          )}
          {!metamaskAddress && (
            <button
              onClick={connectMetamask}
              className="bg-accent px-2 py-1 flex items-center space-x-2 px-3 py-1 bg-accent/10 rounded-full"
            >
               
              <span className="text-sm font-caption text-accent-foreground font-medium">
                Connect Metamask
              </span>
            </button>
          )}
        </div>

        {/* Right Side Actions */}
        <div className="flex items-center space-x-2 ml-auto">
          {/* Tier Status Indicator */}
          {user?.tier && (
            <div className="hidden sm:flex items-center space-x-2 px-3 py-1 bg-accent/10 rounded-full">
              <div className="w-2 h-2 bg-accent rounded-full animate-breathing"></div>
              <span className="text-sm font-caption text-accent-foreground font-medium">
                {user?.tier} Member
              </span>
            </div>
          )}

          {/* Notification Center */}
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsNotificationOpen(!isNotificationOpen)}
              className="relative"
            >
              <Icon name="Bell" size={20} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-error-foreground text-xs rounded-full flex items-center justify-center font-medium">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>

            {/* Notification Dropdown */}
            {isNotificationOpen && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-popover border border-border rounded-lg shadow-wellness-lg z-50">
                <div className="p-4 border-b border-border">
                  <h3 className="font-heading font-medium text-foreground">Notifications</h3>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {notifications?.length > 0 ? (
                    notifications?.slice(0, 5)?.map((notification, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          onNotificationClick?.(notification);
                          setIsNotificationOpen(false);
                        }}
                        className="w-full p-3 text-left hover:bg-muted transition-wellness border-b border-border last:border-b-0"
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`w-2 h-2 rounded-full mt-2 ${notification?.read ? 'bg-muted' : 'bg-accent'}`}></div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {notification?.title}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {notification?.message}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {notification?.time}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="p-4 text-center text-muted-foreground">
                      <Icon name="Bell" size={24} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No notifications yet</p>
                    </div>
                  )}
                </div>
                {notifications?.length > 5 && (
                  <div className="p-3 border-t border-border">
                    <Button variant="ghost" size="sm" className="w-full">
                      View all notifications
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Profile */}
          <Button
            variant="ghost"
            onClick={() => {
              onProfileClick?.();
              handleNavigation('/member-profile');
            }}
            className="flex items-center space-x-2 px-3"
          >
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              <Icon name="User" size={16} color="white" />
            </div>
            <span className="hidden sm:block text-sm font-medium text-foreground">
              {user?.name || 'Member'}
            </span>
          </Button>

          {/* Mobile Menu Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden"
          >
            <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
          </Button>
        </div>
      </div>
      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-border bg-card">
          <nav className="px-4 py-2 space-y-1">
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`flex items-center space-x-3 w-full px-3 py-3 rounded-lg text-sm font-medium transition-wellness ${
                  isActive(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={item?.icon} size={18} />
                <span>{item?.label}</span>
              </button>
            ))}
          </nav>
        </div>
      )}
      {/* Overlay for mobile menu */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      {/* Overlay for notifications */}
      {isNotificationOpen && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => setIsNotificationOpen(false)}
        />
      )}
    </header>
  );
};

export default Header;